<template>
  <tile>
    <p class="title">To-Do List</p>
    <template v-if="productTypeId === ProductIds.LOC || productTypeId === ProductIds.PEL">
      <checklist-item-wrapper
        :title="'Income Verification'"
        :complete="false"
        v-if="showDefaultIncomeSourceSelector && showIncomeVerificationTile"
      >
        <Income
          :applicant="applicant"
          :application="application"
          @incomeSourcesUpdated="getApplicationStips"
        />
      </checklist-item-wrapper>
    </template>
    <template v-if="sortedItemsState.length > 0">
      <template v-if="productTypeId === ProductIds.LOC || productTypeId === ProductIds.PEL">
        <application-checklist-item
          v-for="(item, index) in incomeVerificationChecklistItems"
          :key="index"
          :item="item"
          @handleUploadSuccess="handleUploadSuccess($event)"
          @incomeSourcesUpdated="getApplicationStips"
        />
      </template>
      <application-checklist-item
        v-for="(item, index) in nonIncomeVerificationChecklistItems"
        :key="index"
        :item="item"
        @handleUploadSuccess="handleUploadSuccess($event)"
        @incomeSourcesUpdated="getApplicationStips"
        @addressMismatchStipUpdated="updateChecklistItem($event)"
      />
      <p v-if="showCompletedItems" class="title">Completed Items</p>
      <application-checklist-item
        v-for="(item, index) in sortedItemsState.filter((item: any) => item.complete === true)"
        :key="index"
        :item="item"
      />
    </template>
  </tile>
</template>

<script setup lang="ts">
import { reactive, onMounted, computed } from "vue";
import type { PropType } from "vue";
import { useStore } from "vuex";
import type { Application, Applicant } from "@/models/application";
import Tile from "@/components/shared/Tile.vue";
import ApplicationChecklistItem from "@/components/ApplicationChecklistItem.vue";
import useChecklistHelpers from "@/use/checklistHelpers";
import Income from "@/components/checklist/Income.vue";
import ChecklistItemWrapper from "@/components/checklist/ChecklistItemWrapper.vue";
import { ProductIds } from "@/config/constants";
import { SortedCheckListItem } from "@/models/checklist";

const { updateStipulationStatus, replaceObjectWithId } = useChecklistHelpers();

const props = defineProps({
  applicant: Object as PropType<Applicant>,
  application: Object as PropType<Application>,
});

const emit = defineEmits(["hydrateStore"]);
const store = useStore();

const showCompletedItems = computed<boolean>(
  () => sortedItemsState.value?.filter((item: any) => item.complete === true)?.length > 0,
);
const applicationId = computed<number>(() => store.state.appData.application.id);
const productTypeId = computed<number>(() => store.state.appData.application.productTypeId);
const sortedItemsState = computed<SortedCheckListItem[]>(() => store.state.checklistModule.sortedItems);

const incomeStips = computed(() => {
  if (sortedItemsState?.value && sortedItemsState?.value?.length > 0) {
    let stips = sortedItemsState?.value?.find((s: any) => s.title === "Income Verification")?.items;
    return stips && stips?.length > 0;
  } else {
    return undefined;
  }
});

const showDefaultIncomeSourceSelector = computed(
  () => incomeStips.value === undefined || incomeStips.value === false,
);
const showIncomeVerificationTile = computed(
  () => store.state.checklistModule?.showIncomeVerification === true,
);

const incomeVerificationChecklistItems = computed<SortedCheckListItem[]>(() => {
  return sortedItemsState.value.filter(
    (item: SortedCheckListItem) =>
      item.complete !== true && item.formattedTitle === "Income Verification",
  );
});

const nonIncomeVerificationChecklistItems = computed<SortedCheckListItem[]>(() => {
  return sortedItemsState.value.filter(
    (item: SortedCheckListItem) =>
      item.complete !== true && item.formattedTitle !== "Income Verification",
  );
});

let sortedItems = reactive([] as any[]);

async function getApplicationStips() {
  await store.dispatch("checklistModule/getSortedChecklistItems");
}

async function handleUploadSuccess(event: any) {
  const status =
    store.state.lookupsModule?.lookups?.stipStatuses?.length > 0
      ? store.state.lookupsModule?.lookups?.stipStatuses.find(
          (s: any) => s.name?.trim() === "Pending",
        )
      : false;
  if (applicationId.value) {
    const response = await updateStipulationStatus(applicationId.value, status, event);
    replaceObjectWithId(sortedItems, response.id, response);
  }

  // update the item status
  store.dispatch("checklistModule/getSortedCheklistItemStatuses");
}

function updateChecklistItem(event: any) {
  replaceObjectWithId(sortedItems, event.id, event);
  // update the item status
  store.dispatch("checklistModule/getSortedCheklistItemStatuses");
}

onMounted(async () => {
  await getApplicationStips();
  sortedItems = store.getters["checklistModule/sortedItems"];
});
</script>

<style lang="scss" scoped>
.title {
  font-weight: 700;
  font-size: 1.25rem;
}
</style>
